import React, { useCallback } from 'react';
import {
  AutorenewRounded as AutorenewRoundedIcon,
  UploadFileRounded as UploadFileRoundedIcon,
} from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import {
  GUARANTEES_APPLICATION_FILES_EMPTY_FILE_TEXT,
  GUARANTEES_APPLICATION_FILES_REGENERATE_LABEL,
  GUARANTEES_APPLICATION_FILES_REPLACE_LABEL,
  GUARANTEES_APPLICATION_FILES_UPLOAD_LABEL,
} from './constants.ts';
import ApplicationFileRow from './ApplicationFileRow.tsx';
import type { IApplicationFileTypeItemProps } from './types.ts';
import {
  canShowApplicationFileAction,
  getApplicationFileCapabilityDisplayName,
  getApplicationFileCapabilityFiles,
  getApplicationFileId,
  getFirstApplicationFile,
} from './utils.ts';

const ApplicationFileTypeItem: React.FC<IApplicationFileTypeItemProps> = ({
  activeAction = null,
  capability,
  onDelete,
  onDownload,
  onRegenerate,
  onUpload,
}) => {
  const files = getApplicationFileCapabilityFiles(capability);
  const firstFile = getFirstApplicationFile(capability);
  const firstFileId = getApplicationFileId(firstFile);
  const uploadLabel = capability.isMultipleFiles
    ? GUARANTEES_APPLICATION_FILES_UPLOAD_LABEL
    : firstFileId
      ? GUARANTEES_APPLICATION_FILES_REPLACE_LABEL
      : GUARANTEES_APPLICATION_FILES_UPLOAD_LABEL;
  const isUploadActive =
    activeAction?.action === 'upload' && activeAction.fileType === capability.fileType;
  const isRegenerateActive =
    activeAction?.action === 'regenerate' && activeAction.fileType === capability.fileType;

  const handleUploadChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      event.target.value = '';

      if (file) {
        void onUpload?.(
          capability.fileType,
          file,
          capability.isMultipleFiles ? undefined : firstFile
        );
      }
    },
    [capability.fileType, capability.isMultipleFiles, firstFile, onUpload]
  );

  const getFileRowKey = (fileIndex: number): string =>
    `${capability.fileType}-${getApplicationFileId(files[fileIndex]) ?? fileIndex}`;

  const rowsContent = files.length ? (
    files.map((file, index) => (
      <ApplicationFileRow
        key={getFileRowKey(index)}
        capability={capability}
        file={file}
        fileIndex={index}
        activeAction={activeAction}
        onDownload={onDownload}
        onDelete={onDelete}
      />
    ))
  ) : (
    <Typography sx={guaranteesClientPageStyles.applicationFilesEmptyText}>
      {GUARANTEES_APPLICATION_FILES_EMPTY_FILE_TEXT}
    </Typography>
  );

  return (
    <Box sx={guaranteesClientPageStyles.applicationFilesItem}>
      <Box sx={guaranteesClientPageStyles.applicationFilesItemHeader}>
        <Typography sx={guaranteesClientPageStyles.applicationFilesType}>
          {getApplicationFileCapabilityDisplayName(capability)}
        </Typography>
        <Box sx={guaranteesClientPageStyles.applicationFilesTypeActions}>
          {canShowApplicationFileAction(capability, 'upload') && onUpload && (
            <Button
              component="label"
              size="small"
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              disabled={isUploadActive}
              sx={guaranteesClientPageStyles.applicationFilesActionButton}
            >
              {uploadLabel}
              <Box
                component="input"
                type="file"
                sx={guaranteesClientPageStyles.applicationFilesHiddenInput}
                onChange={handleUploadChange}
              />
            </Button>
          )}
          {canShowApplicationFileAction(capability, 'regenerate') && onRegenerate && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutorenewRoundedIcon />}
              disabled={isRegenerateActive}
              sx={guaranteesClientPageStyles.applicationFilesActionButton}
              onClick={() => {
                void onRegenerate(capability.fileType, firstFile);
              }}
            >
              {GUARANTEES_APPLICATION_FILES_REGENERATE_LABEL}
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={guaranteesClientPageStyles.applicationFilesRows}>{rowsContent}</Box>
    </Box>
  );
};

export default ApplicationFileTypeItem;
